import { useCallback, useEffect, useState } from "react";
import {
  Button,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog,
  Alert,
  Grid,
} from "@mui/material";
import {
  changeUserIdToId,
  checkOfficerPhone,
  checkOfficerEmail,
  TCreateOfficerData,
  TCompanyFormRole,
} from "../../network";
import { TCompany, TCompanyWithRole, TOfficer } from "../../models";
import { TPagingResponse } from "../../../../models/PagingResponse";
import {
  getCompaniesFromIds,
  getCompaniesWithNotifications,
} from "../../utils";
import { useOfficerAction } from "../../hooks/useOfficerAction";
import { useResetUserPassword } from "../../hooks/useResetUserPassword";
import { isEmpty } from "lodash";
import { filterInput, numbersRegExp } from "../../../../utils/validate";
import { DoubleSelectDialog } from "../../../ui/components/MuiSelectDialog";
import { CompanyRole } from "../../../../auth/AuthProvider";
import { ActionType } from "../../../../models/ActionType";

export type Props = {
  onClose: (success: boolean) => void;
  open: boolean;
  companiesList: TPagingResponse<TCompany>;
  selectedOfficer: TOfficer | null;
  setSelectedOfficer: React.Dispatch<React.SetStateAction<TOfficer | null>>;
  actionMode?: ActionType.EDIT | ActionType.CREATE;
  setActionMode: React.Dispatch<
    React.SetStateAction<ActionType.CREATE | ActionType.EDIT>
  >;
};

type FormValues = {
  name: string;
  email: string;
  phone: string;
  company_role: TCompanyFormRole[];
};

const actionsView = {
  create: "Create",
  edit: "Edit",
};

type identityCheck = {
  checked: boolean;
  taken: boolean;
  user: TOfficer | null;
};

export const CreateOfficerDialog = (props: Props) => {
  const {
    onClose,
    open,
    companiesList,
    actionMode = ActionType.CREATE,
    setActionMode,
    setSelectedOfficer,
    selectedOfficer,
  } = props;

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [result, loading, error, request, clearError] = useOfficerAction();
  const [resetResult, resetLoading, resetError, resetRequest, clearResetError] =
    useResetUserPassword();

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [companies, setCompanies] = useState<TCompanyWithRole[] | []>([]);

  const [phoneCheck, setPhoneCheck] = useState<identityCheck>({
    checked: false,
    taken: false,
    user: null,
  });
  const [emailCheck, setEmailCheck] = useState<identityCheck>({
    checked: false,
    taken: false,
    user: null,
  });

  const [errors, setErrors] = useState<any>({});

  const handleCancel = useCallback(
    (success: boolean = false) => {
      setEmail("");
      setPhone("");
      setName("");
      setCompanies([]);
      setErrors({});
      clearError();
      clearResetError();
      setPhoneCheck({ checked: false, taken: false, user: null });
      setEmailCheck({ checked: false, taken: false, user: null });
      onClose(success);
    },
    [onClose, clearError, clearResetError]
  );

  useEffect(() => {
    if (result) {
      handleCancel(true);
    }
  }, [result, handleCancel]);

  useEffect(() => {
    if (resetResult) {
      clearResetError();
    }
  }, [resetResult, clearResetError]);

  useEffect(() => {
    if (resetResult) {
      setSuccessMessage("Password reset successfully");
    }
  }, [resetResult]);

  useEffect(() => {
    if (successMessage) {
      const timeout = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [successMessage]);

  const handleChangePhone = (phone: string) => {
    setPhoneCheck({ checked: false, taken: false, user: null });
    setPhone(phone);
  };

  const handleChangeEmail = (email: string) => {
    setEmailCheck({ checked: false, taken: false, user: null });
    setEmail(email);
  };

  useEffect(() => {
    if (selectedOfficer) {
      setPhoneCheck({ checked: true, taken: false, user: null });
      setEmailCheck({ checked: true, taken: false, user: null });
      setEmail(selectedOfficer.email);
      setPhone(selectedOfficer.phone);
      setName(selectedOfficer.name);
      setCompanies(
        getCompaniesFromIds(companiesList.items, selectedOfficer.companyRole)
      );
    }
  }, [selectedOfficer, companiesList]);

  const switchToEditMode = (user: TOfficer | null) => {
    if (user) {
      setSelectedOfficer(user);
      setActionMode(ActionType.EDIT);
    }
  };

  const checkUserPhone = async () => {
    if (!phone) {
      setPhoneCheck({ checked: true, taken: false, user: null });
      return;
    }

    let newCheckState = { checked: true, taken: false, user: null };
    try {
      let phoneNumber = "+" + phone.split("+").join("");
      const data = await checkOfficerPhone(phoneNumber);
      newCheckState = {
        checked: true,
        taken: true,
        user: changeUserIdToId(data),
      };
    } catch (e: any) {
      newCheckState = { checked: true, taken: false, user: null };
    } finally {
      setPhoneCheck(newCheckState);
    }
  };

  const checkUserEmail = async () => {
    if (!email) {
      setEmailCheck({ checked: true, taken: false, user: null });
      return;
    }

    let newCheckState = { checked: true, taken: false, user: null };
    try {
      const data = await checkOfficerEmail(email);
      const userData = changeUserIdToId(data);
      newCheckState = { checked: true, taken: true, user: userData };
    } catch (e: any) {
      newCheckState = { checked: true, taken: false, user: null };
    } finally {
      setEmailCheck(newCheckState);
    }
  };

  const validate = useCallback((formData: FormValues) => {
    const errors: any = {};

    if (!(formData.email || formData.phone)) {
      errors.email = true;
      errors.phone = true;
    }
    setErrors(errors);
    return errors;
  }, []);

  const onSubmit = useCallback(async () => {
    let phoneNumber = phone ? "+" + phone.split("+").join("") : "";
    const formValues: FormValues = {
      name,
      email,
      phone: phoneNumber,
      company_role: companies.map((company) => ({
        company_id: company.id,
        role: company.role,
        email_notification_enabled: company.emailNotificationEnabled,
      })),
    };
    const validateErrors = validate(formValues);
    if (isEmpty(validateErrors)) {
      const requestData: TCreateOfficerData = formValues;
      if (selectedOfficer) {
        request(requestData, actionMode, selectedOfficer.id);
      } else {
        request(requestData, actionMode);
      }
    }
  }, [
    actionMode,
    companies,
    email,
    phone,
    name,
    request,
    selectedOfficer,
    validate,
  ]);

  return (
    <Dialog
      sx={{ "& .MuiDialog-paper": { width: "80%" } }}
      maxWidth='xs'
      open={open}
      id='create-company-dialog'
      keepMounted
    >
      <DialogTitle sx={{ textTransform: "capitalize" }}>
        {actionsView[actionMode]} compliance officer
      </DialogTitle>
      <DialogContent dividers>
        <Grid container direction='column'>
          <TextField
            disabled={actionMode === ActionType.EDIT}
            error={errors.email}
            label='Email'
            name='email'
            type='email'
            margin='normal'
            size='small'
            value={email}
            onBlur={checkUserEmail}
            inputProps={{
              onChange: (e: any) => {
                delete errors.email;
                return handleChangeEmail(e.target.value);
              },
            }}
          />
          {emailCheck.checked && emailCheck.taken && emailCheck.user && (
            <Alert
              severity='warning'
              action={
                <Button
                  color='inherit'
                  size='small'
                  onClick={() => switchToEditMode(emailCheck.user)}
                >
                  Edit
                </Button>
              }
            >
              User with this email exists. Either <strong>change</strong> the
              email or you can <strong>edit</strong> the user
            </Alert>
          )}
          {actionMode !== ActionType.EDIT &&
            email &&
            emailCheck.checked &&
            !emailCheck.taken && (
              <Alert severity='success'>
                You can create new user with this email
              </Alert>
            )}
          <TextField
            disabled={actionMode === ActionType.EDIT}
            error={errors.phone}
            label='Phone number'
            name='phone'
            size='small'
            type='tel'
            margin='normal'
            value={phone}
            onBlur={checkUserPhone}
            inputProps={{
              onChange: (e: any) => {
                filterInput(
                  (e.target as HTMLTextAreaElement).value,
                  numbersRegExp,
                  handleChangePhone
                );
                delete errors.phone;
              },
            }}
          />
          {phoneCheck.checked && phoneCheck.taken && phoneCheck.user && (
            <Alert
              severity='warning'
              action={
                <Button
                  color='inherit'
                  size='small'
                  onClick={() => switchToEditMode(phoneCheck.user)}
                >
                  Edit
                </Button>
              }
            >
              User with this number exists. Either <strong>change</strong> the
              number or you can <strong>edit</strong> the user
            </Alert>
          )}
          {actionMode !== ActionType.EDIT &&
            phone &&
            phoneCheck.checked &&
            !phoneCheck.taken && (
              <Alert severity='success'>
                You can create new user with this phone number
              </Alert>
            )}
          <TextField
            error={errors.name}
            label='Name'
            name='name'
            type='text'
            margin='normal'
            size='small'
            value={name}
            inputProps={{
              onChange: (e: any) => {
                delete errors.name;
                return setName(e.target.value);
              },
            }}
          />
          <DoubleSelectDialog
            sx={{ marginTop: "8px" }}
            subData={[
              CompanyRole.VIEWER,
              CompanyRole.MANAGER,
              CompanyRole.ADMIN,
              CompanyRole.FINAL_REVIEWER,
            ]}
            disabled={!phoneCheck.checked || !emailCheck.checked}
            nameField='name'
            data={
              selectedOfficer
                ? getCompaniesWithNotifications(
                    companiesList.items,
                    selectedOfficer.companyRole
                  )
                : companiesList.items
            }
            label='Companies/Role'
            name='companyRole'
            onChange={setCompanies}
            values={companies}
          />
          {actionMode === ActionType.EDIT && selectedOfficer && (
            <Button
              sx={{ mt: 3 }}
              onClick={() => resetRequest(selectedOfficer.id)}
              disabled={resetLoading}
            >
              Reset Password
            </Button>
          )}
        </Grid>
        {(error || errors.network) && (
          <Alert sx={{ marginTop: 2 }} severity='error'>
            {(error || errors.network).toString()}
          </Alert>
        )}
        {resetError && (
          <Alert sx={{ marginTop: 2 }} severity='error'>
            {resetError.toString()}
          </Alert>
        )}
        {successMessage && (
          <Alert sx={{ marginTop: 2 }} severity='success'>
            {successMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={() => handleCancel()} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          disabled={loading || phoneCheck.taken || emailCheck.taken}
          variant='contained'
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
